import React, { useState } from 'react';
import { z } from 'zod';
import { styled } from 'styled-components';
import type { BlockDefinition } from '../types';

// Home services — clean rewrite of legacy molecules/OurServicesNew (Strapi
// sections.home-services + components.tabbed-image). Interactive tabbed panel:
// a tab list with a moving rail indicator drives a logos grid. Interactivity
// (active tab) is client-side useState; SSR renders tab 0, hydration wires the
// clicks. No styled-system / next/image / framer-motion.
export const homeServicesSchema = z.object({
  eyebrow: z.string().optional(),
  heading: z.string().min(1),
  description: z.string().optional(),
  tabs: z
    .array(
      z.object({
        title: z.string().min(1),
        description: z.string().optional(),
        logos: z.array(z.string()).default([]),
      }),
    )
    .min(1)
    .max(8),
});

type HomeServicesProps = z.infer<typeof homeServicesSchema>;

const Section = styled.section`
  background: #ffffff;
  max-width: 144rem;
  margin: 0 auto;
  padding: 6rem 1.6rem 4rem;

  @media (min-width: 64em) {
    padding: 12rem 16rem;
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const Eyebrow = styled.p`
  margin: 0;
  color: #1a1b1f;
  font-family: 'DM Sans', sans-serif;
  font-size: 1.5rem;
  font-weight: 400;
  letter-spacing: 5px;
  text-transform: uppercase;
`;

const Heading = styled.h2`
  margin: 0.8rem 0 0;
  color: #0b1020;
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 600;
  font-size: clamp(2.8rem, 4vw, 4.8rem);
  letter-spacing: -2px;
`;

const Description = styled.p`
  max-width: 69rem;
  margin: 0.8rem 0 0;
  color: #4e4f52;
  font-family: 'DM Sans', sans-serif;
  font-size: 1.5rem;
`;

const Body = styled.div`
  display: grid;
  gap: 3rem;
  margin-top: 4rem;

  @media (min-width: 64em) {
    grid-template-columns: 1fr 2.5fr;
    column-gap: 5rem;
    align-items: center;
    margin-top: 8rem;
  }
`;

const TabList = styled.div`
  display: grid;
  grid-template-columns: 8px auto;
  column-gap: 1.6rem;

  @media (min-width: 64em) {
    column-gap: 3.6rem;
  }
`;

const Rail = styled.div`
  position: relative;
  width: 8px;
  min-height: 24rem;
  background: #e8efff;
  border-radius: 2px;
`;

const RailFill = styled.div`
  position: absolute;
  left: 0;
  width: 8px;
  background: #063091;
  border-radius: 2px;
  transition: top 0.4s ease-in-out;
`;

const Tabs = styled.div`
  display: grid;
  row-gap: 1rem;
  align-content: center;
`;

const TabTitle = styled.button<{ $active: boolean }>`
  appearance: none;
  border: none;
  background: none;
  padding: 0;
  text-align: left;
  cursor: pointer;
  font-family: 'DM Sans', sans-serif;
  font-weight: 550;
  font-size: clamp(1.8rem, 2vw, 2rem);
  color: ${({ $active }) => ($active ? '#063091' : '#a3a3a3')};
  transition: color 0.3s ease;
`;

const Panel = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.6rem;
  padding: 2.4rem;
  border-radius: 2.4rem;
  background: linear-gradient(135deg, #eef3ff 0%, #f8faff 100%);

  @media (min-width: 64em) {
    grid-template-columns: repeat(3, 1fr);
    gap: 2.4rem;
    padding: 4rem;
    min-height: 45rem;
    align-content: center;
  }
`;

const LogoCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 10rem;
  background: #ffffff;
  border-radius: 1.6rem;
`;

const Logo = styled.img`
  max-width: 70%;
  max-height: 50%;
  object-fit: contain;
`;

function HomeServices({ eyebrow, heading, description, tabs }: HomeServicesProps) {
  const [active, setActive] = useState(0);
  const current = tabs[active] ?? tabs[0]!;

  return (
    <Section>
      <Header>
        {eyebrow ? <Eyebrow>{eyebrow}</Eyebrow> : null}
        <Heading>{heading}</Heading>
        {description ? <Description>{description}</Description> : null}
      </Header>

      <Body>
        <TabList>
          <Rail>
            <RailFill
              style={{
                top: `${(active * 100) / tabs.length}%`,
                height: `${100 / tabs.length}%`,
              }}
            />
          </Rail>
          <Tabs>
            {tabs.map((tab, i) => (
              <TabTitle
                key={tab.title}
                $active={i === active}
                onClick={() => setActive(i)}
                aria-pressed={i === active}
              >
                {tab.title}
              </TabTitle>
            ))}
          </Tabs>
        </TabList>

        <Panel>
          {current.logos.map((src, i) => (
            <LogoCard key={`${src}-${i}`}>
              <Logo src={src} alt={current.title} />
            </LogoCard>
          ))}
        </Panel>
      </Body>
    </Section>
  );
}

export const homeServicesBlock: BlockDefinition<HomeServicesProps> = {
  type: 'homeServices',
  schema: homeServicesSchema,
  Renderer: HomeServices,
};
